import React, { useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Button, Stack, Modal, Box } from '@mui/material'
import { ActionButtonsSkeleton } from './'
import { useGetCTAButtonsQuery } from 'Services/CTAButtons'
import { useGetLocationModulesQuery } from 'Services/institution'
import { CIF } from 'Components/cif'
import { setCif } from 'Slices/CifSlice'
import { useGetAnalyticsQuery } from 'Services/analytics'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import { hoverColor } from 'Utils/hoverColor'

const ActionButtons = () => {
  const dispatch = useDispatch()
  const [openCif, setOpenCif] = useState(false)
  const [cifCompleted, setCifCompleted] = useState(false)

  const { locId, instId, tourId } = useSelector((state: any) => state.location)

  const { data: analyticsData } = useGetAnalyticsQuery(
    {
      instId,
      locId,
      tourId,
    },
    { skip: !tourId }
  )

  const { data: modulesData, isSuccess: modulesSuccess } =
    useGetLocationModulesQuery({ instId, locId }, { skip: !instId || !locId })

  const { data, isSuccess, isLoading } = useGetCTAButtonsQuery(
    { instId, locId },
    { skip: !instId || !locId || !modulesSuccess }
  )

  const isPersonalizationEnabled = modulesData?.resources.some(
    (module) => module.moduleid === 'virtual_tour_ai'
  )

  const hasRegistrationEnabled = modulesData?.resources.some(
    (module) => module.moduleid === 'registration'
  )

  let sortedCtaButtons

  if (isSuccess) {
    sortedCtaButtons = [...data?.ctaButtons]
      .sort((a, b) =>
        a.actionid === 'register' ? -1 : b.actionid === 'register' ? 1 : 0
      )
      .map((button) => {
        if (button.actionid === 'register') {
          // Only want to display 'Personalize' if module enabled and register button text is not customized
          return { ...button, name: isPersonalizationEnabled ? 'Personalize' : button.name, status: hasRegistrationEnabled }
        } else {
          return button
        }
      })
  }

  if (
    cifCompleted ||
    (analyticsData?.registration?.hasRegisteredUser && isSuccess)
  )
    sortedCtaButtons = sortedCtaButtons.filter(
      (button) => button.actionid !== 'register'
    )

  return (
    <>
      <Modal
        open={openCif}
        onClose={() => {
          setOpenCif(false)
          dispatch(setCif('closed'))
        }}
        aria-labelledby='modal-modal-title'
        aria-describedby='modal-modal-description'
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '90%',
            height: '90%',
            bgcolor: 'background.paper',
            boxShadow: 24,
            border: 0,
            p: 0,
            m: 0,
          }}
        >
          <CIF
            closeModal={() => {
              setOpenCif(false)
              dispatch(setCif('closed'))
            }}
            onFormCompleted={() => {
              setCifCompleted(true)
            }}
          />
        </Box>
      </Modal>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        sx={{
          marginBottom: '20px',
          width: { xs: '80%', sm: 'auto' },
          margin: { xs: 'auto' },
        }}
      >
        {isLoading ? (
          <>
            <ActionButtonsSkeleton />
          </>
        ) : (
          sortedCtaButtons?.map(
            (ctaButton) =>
              ctaButton?.status && (
                <>
                  <Button
                    key={ctaButton.id}
                    sx={{
                      color: ctaButton.font_color,
                      background: ctaButton.bg_color,
                      textTransform: 'none',
                      '&:hover': {
                        background: hoverColor(ctaButton.bg_color),
                      },
                      padding: { xs: '12px' },
                    }}
                    variant='contained'
                    onClick={() => {
                      if (ctaButton.actionid === 'register') {
                        setOpenCif(true)
                        dispatch(setCif('open'))
                      } else {
                        window.location.href = ctaButton.url
                      }
                    }}
                  >
                    {ctaButton.name}
                    {isPersonalizationEnabled &&
                      ctaButton.actionid === 'register' && (
                        <AutoAwesomeIcon
                          sx={{
                            paddingLeft: '3px',
                            color: 'transparent',
                            stroke: 'gold',
                            strokeWidth: 1,
                            transform: 'scale(0.9)',
                          }}
                        />
                      )}
                  </Button>
                </>
              )
          )
        )}
      </Stack>
    </>
  )
}

export default ActionButtons
